// src/Views/Header.js
// 🔥 FIXED: 
// - "Get started" button now routes logged-in users to /app
// - Mobile sheet solid background for iOS
// - STRATEGIC UPDATE: Premium matches → AiAssistant Engine

import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import iPredictLogo from '../assets/images/logo512.png';
import { useAuth } from '../context/AuthContext';
import heroMain from '../assets/images/hero-football-crypto.png';
import NotificationsBell from './NotificationsBell';
import { Capacitor } from '@capacitor/core';

import InstallModal from './InstallModal/InstallModal';

// 🔥 APP STORE LINK
const APP_STORE_URL = "https://apps.apple.com/gh/app/aipredict-football-crypto/id6757129916";

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);

    const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);

    // 🔥 Detect if running as native iOS app (not mobile web)
    const isNativeIOS = Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();

    const isHome = location.pathname === '/' || location.pathname === '/overview';
    const headerClassName = isHome ? 'header' : 'header header--compact';

    const isLoggedIn = !!user;

    const displayName = user?.displayName
        || (user?.email ? user.email.split('@')[0] : '')
        || 'Trader';

    const overviewLabel = isLoggedIn ? 'Dashboard' : 'Overview';

    const handleOverviewClick = () => {
        if (isLoggedIn) {
            navigate('/app');
        } else {
            navigate('/');
        }
    };

    const handleLogoutClick = () => {
        navigate('/signout');
    };

    const handleSigninClick = () => {
        navigate('/signin');
    };

    // 🔥 UPDATED: Launch app now goes to App Store for non-logged in users
    const handleLaunchAppClick = () => {
        if (isLoggedIn) {
            navigate('/app');
        } else {
            window.open(APP_STORE_URL, '_blank');
        }
    };

    // 🔥 NEW: Direct App Store link handler
    const handleDownloadAppClick = () => {
        window.open(APP_STORE_URL, '_blank');
    };

    // 🔥 FIXED: Get Started handler - routes logged-in users to /app
    const handleGetStartedClick = () => {
        if (isLoggedIn) {
            navigate('/app');
        } else {
            navigate('/signup');
        }
    };

    const handleNavClick = (path) => {
        navigate(path);
        setMobileOpen(false);
    };

    const handleAboutClick = () => {
        handleNavClick('/about');
    };

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    return (
        <header className={headerClassName}>

            {/* 🛡️ THE SHIELD: Blocks content from scrolling behind the battery (iOS Native Only) */}
            {isNativeIOS && <div className="status-bar-shield" />}

            {/* background */}
            <div className="header__bg-glow header__bg-glow--one" />
            <div className="header__bg-glow header__bg-glow--two" />
            <div className="header__bg-grid" />

            {/* TOP DECK NAV - PRIMARY ITEMS */}
            <div className="header__nav">
                {/* LEFT: brand */}
                <div className="header__brand">
                    <div
                        className="header__logo-wrap"
                        onClick={() => navigate('/')}
                        style={{ cursor: 'pointer' }}
                    >
                        <img src={iPredictLogo} alt="AiPredict logo" className="header__logo" />
                    </div>

                    <div className="header__brand-copy">
                        <div className="header__brand-title-row">
                            <span className="header__brand-name">AiPredict</span>
                            <span className="header__brand-dot" />
                        </div>
                        <span className="header__brand-tagline">Automate everything</span>
                    </div>
                </div>

                {/* CENTER: desktop nav - PRIMARY LINKS ONLY */}
                <nav className="header__links">
                    <button className="header__link" onClick={handleOverviewClick}>
                        <Icon icon="solar:compass-linear" className="header__link-icon" />
                        <span>{overviewLabel}</span>
                    </button>

                    <button className="header__link" onClick={() => navigate('/crypto')}>
                        <Icon icon="solar:bitcoin-coin-linear" className="header__link-icon" />
                        <span>Crypto AI</span>
                    </button>

                    {/* 🔄 EDIT 1: Football AI now routes to AiAssistant */}
                    <button className="header__link" onClick={() => navigate('/ai-assistant')}>
                        <Icon icon="solar:soccer-linear" className="header__link-icon" />
                        <span>Football AI</span>
                    </button>

                    <button className="header__link" onClick={() => navigate('/ai-assistant')}>
                        <Icon icon="solar:cpu-bolt-linear" className="header__link-icon" />
                        <span>Ai Assistant</span>
                    </button>

                    <button className="header__link" onClick={() => navigate('/how-it-works')}>
                        <Icon icon="solar:book-linear" className="header__link-icon" />
                        <span>How it works</span>
                    </button>

                    <button className="header__link" onClick={() => navigate('/pricing')}>
                        <Icon icon="solar:card-transfer-linear" className="header__link-icon" />
                        <span>Pricing</span>
                    </button>

                    <button className="header__link" onClick={handleAboutClick}>
                        <Icon icon="solar:info-circle-linear" className="header__link-icon" />
                        <span>About</span>
                    </button>
                </nav>

                {/* RIGHT: TOP DECK actions (DESKTOP) */}
                <div className="header__nav-cta header__nav-cta--top">
                    <NotificationsBell />

                    {loading ? (
                        <span className="header__session-check">Loading…</span>
                    ) : isLoggedIn ? (
                        <button
                            className="header__btn header__btn--ghost"
                            type="button"
                            onClick={handleLogoutClick}
                        >
                            <Icon icon="solar:logout-2-linear" className="header__btn-icon" />
                            <span>Logout</span>
                        </button>
                    ) : (
                        <button
                            className="header__btn header__btn--ghost"
                            type="button"
                            onClick={handleSigninClick}
                        >
                            <Icon icon="solar:login-2-linear" className="header__btn-icon" />
                            <span>Sign in</span>
                        </button>
                    )}
                </div>

                {/* 🔥 MOBILE: Bell + Hamburger (RIGHT SIDE) */}
                <div className="header__mobile-actions">
                    {/* 🔔 Notification Bell - Always visible on mobile */}
                    <NotificationsBell />
                    
                    {/* Hamburger Menu */}
                    <button
                        className={`header__menu-toggle ${mobileOpen ? 'header__menu-toggle--open' : ''}`}
                        aria-label="Toggle navigation"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        style={{ border: 'none', background: 'transparent' }} 
                    >
                        <Icon 
                            icon={mobileOpen ? "solar:close-circle-bold" : "solar:hamburger-menu-bold"} 
                            style={{ color: '#FFFFFF', fontSize: '32px' }} 
                        />
                    </button>
                </div>
            </div>

            {/* LOWER DECK (HOMEPAGE ONLY) - Status, Welcome, Secondary Links, Download */}
            {isHome ? (
                <div className="header__nav-lower">
                    {/* LEFT: Status + Welcome */}
                    <div className="header__lower-left">
                        <div className="header__status">
                            <span className="header__status-dot" />
                            <span className="header__status-text">Live predictions 24/7</span>
                        </div>

                        <span className="header__welcome">
                            Welcome,&nbsp;<strong>{isLoggedIn ? displayName : 'Trader'}</strong>
                        </span>
                    </div>

                    {/* CENTER: Secondary Navigation Links */}
                    <div className="header__lower-center">
                        <button className="header__lower-link header__lower-link--referral" onClick={() => navigate('/referral')}>
                            <Icon icon="solar:gift-bold" className="header__lower-link-icon" />
                            <span>Refer & Earn</span>
                        </button>

                        <button className="header__lower-link header__lower-link--guide" onClick={() => navigate('/guide')}>
                            <Icon icon="solar:cup-star-bold" className="header__lower-link-icon" />
                            <span>Profit Guide</span>
                        </button>

                        <button className="header__lower-link" onClick={() => navigate('/faq')}>
                            <Icon icon="solar:question-circle-linear" className="header__lower-link-icon" />
                            <span>FAQ</span>
                        </button>

                        <button className="header__lower-link" onClick={() => navigate('/legal')}>
                            <Icon icon="solar:document-text-linear" className="header__lower-link-icon" />
                            <span>Legal & Risk</span>
                        </button>
                    </div>

                    {/* RIGHT: Download Button */}
                    <button
                        className="header__btn header__btn--primary"
                        onClick={handleLaunchAppClick}
                    >
                        <Icon icon="simple-icons:apple" className="header__btn-icon" />
                        <span>{isLoggedIn ? 'Launch app' : 'Download App'}</span>
                        <Icon icon="solar:arrow-right-up-linear" className="header__btn-icon" />
                    </button>
                </div>
            ) : null}

            {/* mobile sheet */}
            <div className={`header__mobile-sheet ${mobileOpen ? 'header__mobile-sheet--open' : ''}`}>
                <nav className="header__mobile-links">
                    {/* PRIMARY SECTION */}
                    <span className="header__mobile-section-label">Main</span>
                    
                    <button className="header__mobile-link" onClick={handleOverviewClick}>
                        <Icon icon="solar:compass-linear" className="header__mobile-link-icon" />
                        <span>{overviewLabel}</span>
                    </button>
                    <button className="header__mobile-link" onClick={() => handleNavClick('/crypto')}>
                        <Icon icon="solar:bitcoin-coin-linear" className="header__mobile-link-icon" />
                        <span>Crypto AI</span>
                    </button>
                    {/* 🔄 EDIT 2: Football AI now routes to AiAssistant */}
                    <button className="header__mobile-link" onClick={() => handleNavClick('/ai-assistant')}>
                        <Icon icon="solar:soccer-linear" className="header__mobile-link-icon" />
                        <span>Football AI</span>
                    </button>
                    <button className="header__mobile-link" onClick={() => handleNavClick('/ai-assistant')}>
                        <Icon icon="solar:cpu-bolt-linear" className="header__mobile-link-icon" />
                        <span>Ai Assistant</span>
                    </button>
                    
                    <button className="header__mobile-link" onClick={() => handleNavClick('/how-it-works')}>
                        <Icon icon="solar:book-linear" className="header__mobile-link-icon" />
                        <span>How it works</span>
                    </button>
                    <button className="header__mobile-link" onClick={() => handleNavClick('/pricing')}>
                        <Icon icon="solar:card-transfer-linear" className="header__mobile-link-icon" />
                        <span>Pricing</span>
                    </button>

                    {/* SECONDARY SECTION */}
                    <span className="header__mobile-section-label">More</span>

                    {/* 🔥 Refer & Earn */}
                    <button className="header__mobile-link header__mobile-link--referral" onClick={() => handleNavClick('/referral')}>
                        <Icon icon="solar:gift-bold" className="header__mobile-link-icon" />
                        <span>Refer & Earn</span>
                        <span className="header__mobile-link-badge">NEW</span>
                    </button>

                    {/* 🎯 Profit Guide */}
                    <button className="header__mobile-link header__mobile-link--guide" onClick={() => handleNavClick('/guide')}>
                        <Icon icon="solar:cup-star-bold" className="header__mobile-link-icon" />
                        <span>Profit Guide</span>
                        <span className="header__mobile-link-badge header__mobile-link-badge--green">HOT</span>
                    </button>

                    <button className="header__mobile-link" onClick={() => handleNavClick('/faq')}>
                        <Icon icon="solar:question-circle-linear" className="header__mobile-link-icon" />
                        <span>FAQ</span>
                    </button>

                    <button className="header__mobile-link" onClick={handleAboutClick}>
                        <Icon icon="solar:info-circle-linear" className="header__mobile-link-icon" />
                        <span>About</span>
                    </button>
                    
                    <button className="header__mobile-link" onClick={() => handleNavClick('/legal')}>
                        <Icon icon="solar:document-text-linear" className="header__mobile-link-icon" />
                        <span>Legal & Risk</span>
                    </button>
                </nav>

                <div className="header__mobile-footer">
                    {loading ? (
                        <span className="header__mobile-status">Checking session…</span>
                    ) : isLoggedIn ? (
                        <>
                            <span className="header__mobile-status">
                                Signed in as <strong>{displayName}</strong>
                            </span>
                            <button
                                className="header__btn header__btn--ghost header__btn--full"
                                type="button"
                                onClick={() => {
                                    handleLogoutClick();
                                    setMobileOpen(false);
                                }}
                            >
                                <Icon icon="solar:logout-2-linear" className="header__btn-icon" />
                                <span>Logout</span>
                            </button>
                        </>
                    ) : (
                        <button
                            className="header__btn header__btn--primary header__btn--full"
                            type="button"
                            onClick={() => {
                                handleSigninClick();
                                setMobileOpen(false);
                            }}
                        >
                            <Icon icon="solar:login-2-linear" className="header__btn-icon" />
                            <span>Sign in</span>
                        </button>
                    )}
                </div>
            </div>

            {/* hero section — only show on Overview (/) */}
            {isHome && (
                <div className="header__hero">
                    <div className="header__hero-left">
                        <div className="header__hero-image">
                            <img src={heroMain} alt="AiPredict hero" className="header__hero-image-img" />
                        </div>
                        <p className="header__hero-eyebrow">
                            <Icon icon="solar:cpu-bolt-linear" className="header__eyebrow-icon" />
                            <span>Football • Crypto • Automation</span>
                        </p>
                        <h1 className="header__hero-title">
                            AI-POWERED
                            <span className="header__hero-highlight">PREDICTIONS</span>
                        </h1>
                        <p className="header__hero-subtitle">
                            For every slip you build. Combine football models, market patterns,
                            and crypto AI agent trading in one platform.
                            No spreadsheets, no overthinking &mdash; just clean probabilities and clear decisions.
                        </p>
                        <div className="header__hero-actions">
                            {/* 🔥 FIXED: Get Started now checks login state */}
                            <button className="header__btn header__btn--primary header__btn--lg" onClick={handleGetStartedClick}>
                                <Icon icon="solar:flash-linear" className="header__btn-icon" />
                                <span>{isLoggedIn ? 'Go to Dashboard' : 'Get started'}</span>
                            </button>
                            <button className="header__btn header__btn--ghost header__btn--lg" onClick={() => navigate('/crypto')}>
                                <Icon icon="solar:bitcoin-coin-linear" className="header__btn-icon" />
                                <span>View Crypto AI</span>
                            </button>
                            {/* 🔄 EDIT 3: Changed from "View Premium matches" to "Build Slips with AI" */}
                            <button className="header__btn header__btn--ghost header__btn--lg" onClick={() => navigate('/ai-assistant')}>
                                <Icon icon="solar:cpu-bolt-linear" className="header__btn-icon" />
                                <span>Build Slips with AI</span>
                            </button>
                        </div>
                        <div className="header__logos">
                            <p className="header__logos-label">Works with your favorite platforms</p>
                            <div className="header__logos-row">
                                <div className="header__logo-chip"><Icon icon="simple-icons:binance" className="header__logo-chip-icon" /></div>
                                <div className="header__logo-chip"><Icon icon="simple-icons:coinbase" className="header__logo-chip-icon" /></div>
                                <div className="header__logo-chip"><Icon icon="simple-icons:betfair" className="header__logo-chip-icon" /></div>
                            </div>
                        </div>
                    </div>

                    <div className="header__hero-mid">
                        {/* 🔥 NEW: Download App Card at the top */}
                        <div 
                            className="header__download-card"
                            onClick={handleDownloadAppClick}
                        >
                            <div className="header__download-card-icon">
                                <Icon 
                                    icon="simple-icons:apple" 
                                    style={{ fontSize: '32px', color: '#000' }} 
                                />
                            </div>
                            <div className="header__download-card-text">
                                <p className="header__download-card-label">Download on the</p>
                                <p className="header__download-card-title">App Store</p>
                            </div>
                            <Icon 
                                icon="solar:arrow-right-up-linear" 
                                style={{ fontSize: '24px', color: '#9ca3af' }} 
                            />
                        </div>

                        <div className="header__metric">
                            <span className="header__metric-label">01</span>
                            <div className="header__metric-body">
                                <p className="header__metric-title"><Icon icon="solar:soccer-linear" className="header__metric-icon" /><span>Smart football models</span></p>
                                <p className="header__metric-text">Predict match outcomes, multigoals, and patterns with AI-tuned signals.</p>
                            </div>
                        </div>
                        <div className="header__metric">
                            <span className="header__metric-label">02</span>
                            <div className="header__metric-body">
                                <p className="header__metric-title"><Icon icon="solar:bitcoin-coin-linear" className="header__metric-icon" /><span>Ignis AI Agent</span></p>
                                <p className="header__metric-text">Ignis AI Agent trades crypto on your exchange account autonomously, 24/7.</p>
                            </div>
                        </div>
                        <div className="header__metric">
                            <span className="header__metric-label">03</span>
                            <div className="header__metric-body">
                                <p className="header__metric-title"><Icon icon="solar:infinite-linear" className="header__metric-icon" /><span>Automated routines</span></p>
                                <p className="header__metric-text">Set your rules once &amp; let AiPredict watch markets 24/7.</p>
                            </div>
                        </div>
                    </div>

                    <div className="header__hero-right header__hero-right--stacked">
                        <p className="header__quick-label">Get started in one click</p>
                        
                        {/* 🔄 Football AI Card - Routes to AiAssistant */}
                        <div className="header__quick-card header__quick-card--football">
                            <div className="header__quick-card-header">
                                <div><p className="header__quick-eyebrow">Football AI</p><p className="header__quick-title">AiAssistant Engine</p></div>
                                <Icon icon="solar:cpu-bolt-linear" className="header__quick-icon" />
                            </div>
                            <p className="header__quick-text">Build your own slips with V10.0, HRHR value picks, and Basketball AI. Analyze any match with our powerful prediction engine.</p>
                            <button type="button" className="header__quick-btn" onClick={() => navigate('/ai-assistant')}>Open AiAssistant<Icon icon="solar:alt-arrow-right-linear" className="header__quick-btn-icon" /></button>
                        </div>
                        
                        {/* Crypto AI Card */}
                        <div className="header__quick-card header__quick-card--crypto">
                            <div className="header__quick-card-header">
                                <div><p className="header__quick-eyebrow">Crypto AI</p><p className="header__quick-title">Ignis</p></div>
                                <Icon icon="solar:chart-line-up-linear" className="header__quick-icon" />
                            </div>
                            <p className="header__quick-text">Let Ignis AI Agent trade crypto on your exchange account. Institutional-grade market analysis, AI oversight, and autonomous risk management.</p>
                            <button type="button" className="header__quick-btn" onClick={() => navigate('/crypto')}>View Crypto AI<Icon icon="solar:alt-arrow-right-linear" className="header__quick-btn-icon" /></button>
                        </div>
                    </div>
                </div>
            )}

            {/* Render the Modal */}
            <InstallModal 
                isOpen={isInstallModalOpen} 
                onClose={() => setIsInstallModalOpen(false)} 
            />
        </header>
    );
};

export default Header;